import {
	agentCanViewInteraction,
	isInteractionFiltered,
} from "@/common/helpers";
import { ServerEvent } from "@/common/notification";
import {
	InteractionFilterState,
	useInteractionFilterStore,
} from "@/common/stores/nonpersistingStores/interactionfilter";
import { useQueuesStore, QueuesState } from "@/common/stores/nonpersistingStores/queues";
import { InteractionType } from "@/common/types";
import { notificationListener } from "@/hooks/notification";
import {
	InteractionDto,
	InteractionFilterData,
	InteractionOperations,
	InteractionStatus,
	Queue,
	QueueChanged,
} from "@/repository/models";
import { fetchAllWaitingInteractionsFn } from "@/repository/queryFns/supervisorInteractions";
import { QueryClient, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo } from "react";

export const useWaitingInteractions = (afterDataFetch?: () => void) => {
	const filterData = useInteractionFilterStore(
		(state: InteractionFilterState) => state.waiting
	);
	const supervisorWaitingQueryKey = [
		"supervisorWaitingInteractions",
		filterData,
	];

	const queues = useQueuesStore((state: QueuesState) => state.queues);
	const isQueuesDataLoaded = useQueuesStore((state: QueuesState) => state.isDataLoaded);

	const fetchSupervisorWaitingInteractionsCallback = useCallback(
		(_: any) => {
			const result = fetchAllWaitingInteractionsFn(filterData);

			if (afterDataFetch) {
				afterDataFetch();
			}

			return result;
		},
		[filterData, supervisorWaitingQueryKey]
	);

	const { data: waitingInteractions } = useQuery<InteractionDto[]>({
		queryKey: supervisorWaitingQueryKey,
		queryFn: (_: any) => fetchSupervisorWaitingInteractionsCallback(filterData),
		gcTime: 0,
		refetchOnMount: "always",
	});

	const nonVoicemailInteractions = useMemo(() => {
		return waitingInteractions?.filter(
			(wi) => wi.type !== InteractionType.Voicemail
		);
	}, [waitingInteractions]);

	const voicemailInteractions = useMemo(() => {
		return waitingInteractions?.filter(
			(wi) => wi.type === InteractionType.Voicemail
		);
	}, [waitingInteractions]);

	const queryClient = useQueryClient();

	// When queues finish loading, re-fetch waiting interactions to recover any SignalR
	// events that were dropped while queues were empty 
	useEffect(() => {
		if (isQueuesDataLoaded) {
			queryClient.invalidateQueries({ queryKey: supervisorWaitingQueryKey });
		}
	}, [isQueuesDataLoaded]);

	notificationListener(ServerEvent.UserMembershipChanged, (_: Queue) => {
		queryClient.invalidateQueries({ queryKey: supervisorWaitingQueryKey });
	});

	notificationListener(ServerEvent.UserMembershipRemoved, (_: Queue) => {
		queryClient.invalidateQueries({ queryKey: supervisorWaitingQueryKey });
	});

	notificationListener(ServerEvent.SupervisedAgentRefreshed, (_: Queue) => {
		queryClient.invalidateQueries({ queryKey: supervisorWaitingQueryKey });
	});

	notificationListener<QueueChanged>(ServerEvent.QueueChanged, () => {
		queryClient.invalidateQueries({ queryKey: supervisorWaitingQueryKey });
	});

	const modifyParkedInteraction = (interaction: InteractionDto) => {
		if (!waitingInteractions) {
			return interaction;
		}

		const parkedInteraction = waitingInteractions.find(
			(x) => x.id === interaction.id && x.status == InteractionStatus.Parked
		);
		if (
			parkedInteraction &&
			interaction.status == InteractionStatus.Offering &&
			interaction.changeType !== InteractionOperations.Removed
		) {
			interaction.changeType = InteractionOperations.Updated;
		}

		return interaction;
	};

	const modifyCallPickUpOfferingInteraction = useCallback(
		(interaction: InteractionDto) => {
			if (!waitingInteractions) {
				return interaction;
			}

			const currentInteraction = waitingInteractions.find(
				(x) => x.id === interaction.id
			);

			if (!currentInteraction) {
				return interaction;
			}

			const isInteractionPickUpEnabled =
				queues.find((x) => x.id == interaction.queueId)?.isPickupEnabled ??
				false;

			if (
				interaction.status === InteractionStatus.Offering &&
				interaction.changeType === InteractionOperations.Added &&
				isInteractionPickUpEnabled
			) {
				interaction.changeType = InteractionOperations.Updated;
			}

			return interaction;
		},
		[queues, waitingInteractions]
	);

	const agentCanRemoveIfNeeded = (interaction: InteractionDto) => {
		if (
			interaction.changeType !== InteractionOperations.Removed ||
			!waitingInteractions
		) {
			return false;
		}

		const interactionToRemove = waitingInteractions.find(
			(x) => x.id === interaction.id
		);

		return interactionToRemove !== undefined;
	};

	const addInteraction = (
		contextQueryClient: QueryClient,
		interactionChanged: InteractionDto,
		queryKey: (string | InteractionFilterData)[]
	) => {
		contextQueryClient.setQueryData<InteractionDto[]>(queryKey, (oldData) => {
			if (oldData?.find((i) => i.id == interactionChanged.id)) {
				return oldData;
			}

			return [...(oldData ?? []), interactionChanged];
		});
	};

	const updateInteraction = (
		contextQueryClient: QueryClient,
		interactionChanged: InteractionDto,
		queryKey: (string | InteractionFilterData)[]
	) => {
		contextQueryClient.setQueryData<InteractionDto[]>(queryKey, (oldData) =>
			oldData?.map((item) =>
				item.id === interactionChanged.id
					? { ...item, ...interactionChanged }
					: item
			)
		);
	};

	const removeInteraction = (
		contextQueryClient: QueryClient,
		interactionChanged: InteractionDto,
		queryKey: (string | InteractionFilterData)[]
	) => {
		contextQueryClient.setQueryData<InteractionDto[]>(queryKey, (oldData) =>
			oldData?.filter((item) => item.id !== interactionChanged.id)
		);
	};

	const updateWaitingList = useCallback(
		(interactionChanged: InteractionDto) => {
			switch (interactionChanged.changeType) {
				case InteractionOperations.Added:
					addInteraction(
						queryClient,
						interactionChanged,
						supervisorWaitingQueryKey
					);
					break;
				case InteractionOperations.Updated:
					updateInteraction(
						queryClient,
						interactionChanged,
						supervisorWaitingQueryKey
					);
					break;
				case InteractionOperations.Removed:
					removeInteraction(
						queryClient,
						interactionChanged,
						supervisorWaitingQueryKey
					);
					break;
				default:
					break;
			}
		},
		[queryClient, supervisorWaitingQueryKey]
	);

	notificationListener<InteractionDto>(
		ServerEvent.WaitingCallChanged,
		(interaction: InteractionDto) => {
			if (
				(agentCanViewInteraction(queues, interaction) ||
					agentCanRemoveIfNeeded(interaction)) &&
				!isInteractionFiltered(filterData, interaction)
			) {
				interaction = modifyParkedInteraction(interaction);
				interaction = modifyCallPickUpOfferingInteraction(interaction);

				updateWaitingList(interaction);
			}
		}
	);

	notificationListener<InteractionDto>(
		ServerEvent.SupervisedActiveInteractionChanged,
		(interaction: InteractionDto) => {
			if (
				(agentCanViewInteraction(queues, interaction) ||
					agentCanRemoveIfNeeded(interaction)) &&
				!isInteractionFiltered(filterData, interaction) &&
				interaction.status === InteractionStatus.Parked
			) {
				if (waitingInteractions) {
					const parkedInteractionExists = waitingInteractions.some(
						(x) => x.id === interaction.id
					);

					interaction.changeType =
						parkedInteractionExists &&
						interaction.changeType === InteractionOperations.Added
							? InteractionOperations.Updated
							: InteractionOperations.Added;
				}

				updateWaitingList(interaction);
			}

			//handling the case when ParkedCall is Unparked and it should be removed from the Waiting list view,
			//but the notification is received as for an Active interaction because this is how it is perceived in the app
			if (
				interaction.changeType === InteractionOperations.Removed &&
				interaction.type === InteractionType.Call &&
				waitingInteractions?.some(
					(x) =>
						x.id === interaction.id &&
						x.status === InteractionStatus.Parked
				)
			) {
				updateWaitingList(interaction);
			}
		}
	);

	notificationListener<InteractionDto>(
		ServerEvent.WaitingMessagingChanged,
		(interaction: InteractionDto) => {
			if (
				(agentCanViewInteraction(queues, interaction) ||
					agentCanRemoveIfNeeded(interaction)) &&
				!isInteractionFiltered(filterData, interaction)
			) {
				updateWaitingList(interaction);
			}
		}
	);

	notificationListener<InteractionDto>(
		ServerEvent.WaitingVoicemailChanged,
		(interaction: InteractionDto) => {
			if (!isInteractionFiltered(filterData, interaction)) {
				updateWaitingList(interaction);
			}
		}
	);

	return {
		waitingInteractions,
		nonVoicemailInteractions,
		voicemailInteractions,
	};
};
